/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

import { IHdrMatch } from 'generated-types';
import { HdrMatch } from 'object-types';
import { StringService } from 'ng/modules/core/services/string/string.service';
import { GenericHdrMatchBaseConfigItem } from './generic-hdr-match-base.config-item';

/**
 * @description  HdrMatch ConfigItem class.
 * @author alextsg, Abhinesh Gour, Umesh Chovatiya
 */

export interface IHeaderMatchCriteriaValues {
    stringGroupRefs?: string[];
    valueStrings?: string[];
}

export class HdrMatchConfigItem extends GenericHdrMatchBaseConfigItem<IHdrMatch> {
    public static ajsDependencies = [
        'stringService',
    ];

    /**
     * StringService instance.
     */
    private readonly stringService: StringService;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: HdrMatch,
            ...args,
        };

        super(extendedArgs);

        this.stringService = this.getAjsDependency_('stringService');
    }

    /**
     * Getter for the match_str repeated strings config property.
     */
    public get valueStr(): string[] {
        return this.config.value;
    }

    /**
     * Setter for the match_str repeated strings config property.
     */
    public set valueStr(valueStr: string[] | undefined) {
        this.config.value = valueStr;
    }

    /**
     * Getter for the string_group_refs repeated strings config property.
     */
    public get stringGroupRefs(): string[] {
        return this.config.string_group_refs;
    }

    /**
     * Setter for the string_group_refs repeated strings config property.
     */
    public set stringGroupRefs(groupRefs: string[] | undefined) {
        this.config.string_group_refs = groupRefs;
    }

    public get valueStrings(): string {
        const { value = [] } = this.config;

        return value.join(', ');
    }

    /**
     * Returns string_group_refs as comma seperated names.
     */
    public get stringGroupNames(): string[] {
        const { string_group_refs: stringGroupRefs = [] } = this.config;

        return stringGroupRefs.map(stringGroupRef => this.stringService.name(stringGroupRef));
    }

    /**
     * Clear configured match strings and string group refs.
     */
    public clearStringGroupRefsAndValueStrings(): void {
        this.stringGroupRefs = undefined;
        this.valueStr = undefined;
    }

    /**
     * Returns a string representation of the config data.
     */
    public toString(): string {
        const {
            valueStrings,
            stringGroupNames = [],
        } = this;

        let displayValues = valueStrings;

        if (stringGroupNames.length) {
            displayValues =
                `${displayValues ? `${displayValues}, ` : ''}${stringGroupNames.join(', ')}`;
        }

        return `${displayValues}`;
    }

    /**
     * Clears configured match strings and string group refs.
     */
    public clearStringGroupRefsAndMatchStrings(): void {
        this.stringGroupRefs = undefined;
        this.valueStr = undefined;
    }

    /**
     * Called to add a list of value strings to the config.
     */
    public addValueStrings(valueStrings: string[] = []): void {
        valueStrings.forEach(valueString => {
            this.valueStr = this.valueStr ?? [];
            this.valueStr.push(valueString);
        });
    }

    /**
     * Called to add a list of string group refs to the config.
     */
    public addStringGroupRefs(groupRefs: string[] = []): void {
        groupRefs.forEach(groupRef => {
            this.stringGroupRefs = this.stringGroupRefs ?? [];
            this.stringGroupRefs.push(groupRef);
        });
    }
}
